import { ListItemText, Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { Dayjs } from 'dayjs';
import { cloneDeep, memoize } from 'lodash';
import type { ReactElement } from 'react';
import { createSearchParams } from 'react-router';

import { getTextFromElement } from '@/components/charts/charts.utils';
import type { GenericChartWidgetDataProps } from '@/components/GenericChartWidget/GenericChartWidget.types';
import PriorityChip from '@/components/PriorityChip';
import StatusChip from '@/components/StatusChip';
import getDateTime from '@/utils/getDateTime.utils';

function getDatesPrivate(now: Dayjs, timeFrame: number) {
  // Start date is current date minus timeFrame
  const startDate = now.subtract(timeFrame, 'days');
  const stopDate = now;
  const dateArray = [];
  let currentDate = startDate;
  while (currentDate <= stopDate) {
    dateArray.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'days');
  }
  return dateArray;
}

function getTimestampsPrivate(now: Dayjs, timeFrame: number) {
  // Start date is current date minus timeFrame
  const startDate = now.subtract(timeFrame, 'days');
  const stopDate = now;
  const dateArray = [];
  let currentDate = startDate.startOf('day');

  while (currentDate <= stopDate) {
    dateArray.push(currentDate.toDate().getTime());
    currentDate = currentDate.add(1, 'days').startOf('day');
  }
  return dateArray;
}

export const getTimestamps = memoize(
  getTimestampsPrivate,
  (now, timeFrame) => now.format('YYYY-MM-DD') + timeFrame
);

// getDates is memoized to avoid recalculating the same dates for multiple widgets
export const getDates = memoize(
  getDatesPrivate,
  (now, timeFrame) => now.format('YYYY-MM-DD') + timeFrame
);
export default getDates;

export const widgetSearchParams = (title: string, timeFilter: string) => {
  switch (title) {
    case 'Open Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({ ticket_status__name: ['__in', 'Open'] })
      }).toString();
    case 'Unresolved Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_status__name: ['__in', 'Open,Acknowledged']
        })
      });
    case 'High Priority Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_priority__name: ['__in', 'P1,P2'],
          ticket_status__name: ['__in', 'Open,Acknowledged']
        })
      });
    case 'Open Threat Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_status__name: ['__in', 'Open'],
          ticket_type: ['__eq', 1]
        })
      });
    case 'Unresolved Threat Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_status__name: ['__in', 'Open,Acknowledged'],
          ticket_type: ['__eq', 1]
        })
      });
    case 'High Priority Threat Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_priority__name: ['__in', 'P1,P2'],
          ticket_type: ['__eq', 1],
          ticket_status__name: ['__in', 'Open,Acknowledged']
        })
      });
    case 'Recent Service Tickets':
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_type: ['__eq', 2],
          ticket_status__name: ['__in', 'Open,Acknowledged']
        })
      });
    case 'Avg. New Tickets':
      return createSearchParams({
        // For average new tickets, we want to show all tickets created in the time frame
        t: timeFilter,
        f: JSON.stringify({
          ticket_status__name: ['__in', 'Open,Acknowledged']
        })
      });
    default:
      return createSearchParams({
        t: timeFilter,
        f: JSON.stringify({
          ticket_type: ['__eq', 1],
          ticket_status__name: ['__in', 'Open,Acknowledged']
        })
      });
  }
};

export const convertToWords = (number: number) => {
  if (number >= 1000) {
    const units = ['', 'thousand', 'million', 'billion', 'trillion'];
    const exponent = Math.floor(Math.log10(number) / 3);
    const suffix = units[exponent - 1];
    const base = number / 10 ** (exponent * 3);
    return `${base.toFixed(0)} ${suffix}`;
  }
  return number;
};

const getGaugeText = (percent: number) => {
  if (percent < 60 || Number.isNaN(percent)) {
    return 'is within';
  }
  if (percent < 95 && percent >= 60) {
    return 'is close to';
  }
  if (percent >= 95 && percent < 100) {
    return 'is very close to';
  }
  if (percent === 100) {
    return 'is at';
  }
  if (percent > 100) {
    return 'exceeds';
  }
  return '';
};

export const formatUsageGaugeText = (
  timeFrame: number,
  average: number,
  percent: number,
  type: string,
  limit: number | null
) => {
  const units = type === 'log' ? 'GB/day' : 'Mbps';
  return (
    <div>
      Your {timeFrame}-day {type}{' '}
      {type === 'network' ? 'bandwidth usage' : 'ingestion'} average of{' '}
      <b>
        {average >= 0 ? average : 0} {units}
      </b>{' '}
      {getGaugeText(percent)} your licensed{' '}
      <b>
        {typeof limit === 'number' ? limit : '--'} {units}
      </b>{' '}
      limit.
    </div>
  );
};

/**
 * Extracts limit message from description ReactElement
 * @param description - The ReactElement containing limit information
 * @param dataType - The type of data (e.g., 'Log', 'Network')
 * @returns Formatted limit message string or undefined if no match
 */
export const extractLimitMessage = (
  description: ReactElement,
  dataType: string
): string | undefined => {
  const descriptionText = getTextFromElement(description);
  const regex = /(?<=licensed\s+)\d+\s+\w+(?:\/\w+)?/;
  const match = descriptionText.match(regex);
  if (match) {
    return `Your ${dataType} Bandwidth Limit is ${match[0]}`;
  }
  return undefined;
};

export const announcementsColumns: GridColDef[] = [
  {
    field: 'title',
    headerName: 'Title',
    flex: 1,
    renderCell: (params) => (
      <div>
        <Stack>
          <Typography variant="body2" color="textSecondary">
            {getDateTime(params.row.published_at)}
          </Typography>
          <Typography variant="subtitle2">{params.value}</Typography>
        </Stack>
        <ListItemText
          primary={
            params.row.body.length > 250
              ? `${params.row.body.slice(0, 250)} ...`
              : params.row.body
          }
        />
      </div>
    )
  }
];

export const createTicketColumns = (title: string) => {
  return [
    {
      field: title,
      width: 150,
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <ListItemText
            primary={params.data.title}
            secondary={getDateTime(params.data.published_at)}
          />
        );
      },
      suppressHeaderMenuButton: true,
      resizable: false
    },
    {
      field: 'status_name',
      headerName: '',
      width: 150,
      cellRenderer: (params: ICellRendererParams) => (
        <StatusChip status={params.value} />
      ),
      suppressHeaderMenuButton: true,
      resizable: false
    },
    {
      field: 'priority_name',
      headerName: '',
      width: 90,
      cellRenderer: (params: ICellRendererParams) => (
        <PriorityChip priority={params.value} />
      ),
      suppressHeaderMenuButton: true,
      resizable: false
    }
  ];
};

export const handleCyberScoreCardColors = (value: number) => {
  if (value === 0) {
    return 'info';
  }
  if (value >= 0.9) {
    return 'success';
  }
  if (value > 0.5 && value < 0.9) {
    return 'warning';
  }
  if (value <= 0.5 && value >= 0) {
    return 'error';
  }
  return 'info';
};

export const truncateOSResults = (results: GenericChartWidgetDataProps[]) => {
  const resultsCopy: GenericChartWidgetDataProps[] = cloneDeep(results);
  const topResults = resultsCopy.slice(0, 4);
  // If there are more than 5 results, group the rest into 'Other'
  if (resultsCopy.length > 5) {
    topResults.push({
      name: 'Other',
      count: resultsCopy
        .slice(4)
        .reduce((acc, current: GenericChartWidgetDataProps) => {
          if (
            Array.isArray(current.count) &&
            typeof current.count[0] === 'number'
          ) {
            return acc + current.count[0];
          }
          if (typeof current.count === 'number') {
            return acc + current.count;
          }
          return acc;
        }, 0)
    });
  }
  return topResults;
};

export const processAndTruncateOSAssets = (
  rawResults: Array<{
    name: string;
    color: string;
    count: number;
  }>
) => {
  let results = [...rawResults];

  // Truncate if more than 5 results - keep top 4 and group rest as 'Other'
  if (results.length > 5) {
    const topResults = results.slice(0, 4);
    const otherCount = results
      .slice(4)
      .reduce((acc, current) => acc + current.count, 0);

    results = [
      ...topResults,
      {
        name: 'Other',
        color: results[0]?.color || '',
        count: otherCount
      }
    ];
  }

  // Handle empty names by replacing with 'No Operating System'
  results = results.map((obj) => {
    if (obj?.name === '') {
      return { ...obj, name: 'No Operating System' };
    }
    return obj;
  });

  return results;
};

export const calcChangePercentage = (current: number, prev: number) => {
  if (current !== 0 && prev !== 0) {
    return ((prev - current) / prev) * -100;
  }
  if (current === 0 && prev !== 0) {
    return -100;
  }
  if (current !== 0 && prev === 0) {
    return 100;
  }
  return 0;
};
